/**
 * RosterSelector - Anwesenheits-Auswahl vor Arbeitsbeginn
 *
 * Ermoeglicht dem Vorarbeiter:
 * - Alle Teammitglieder zu sehen
 * - Pro Mitarbeiter Status zu waehlen: Anwesend / Krank / Urlaub
 * - Arbeitstag fuer alle zu starten
 */

import { useEffect } from 'preact/hooks';
import { computed } from '@preact/signals';
import { Users, UserCheck } from 'lucide-preact';
import type { AbsenceReason } from '@timetrack/domain';
import {
  teamMembers,
  rosterSelection,
  setRosterSelection,
  getRosterEntries,
  startDay,
  isLoading,
} from '../../stores/timesheet.store';
import { SwipeButton } from '../../components/ui/SwipeButton';
import styles from './RosterSelector.module.css';

interface RosterSelectorProps {
  onStarted?: () => void;
}

export function RosterSelector({ onStarted }: RosterSelectorProps) {
  // Initialisiere alle Mitarbeiter als "present" wenn noch keine Auswahl
  useEffect(() => {
    if (rosterSelection.value.size === 0 && teamMembers.value.length > 0) {
      const initial = new Map<string, AbsenceReason>();
      teamMembers.value.forEach((emp) => {
        initial.set(emp.id, 'present');
      });
      rosterSelection.value = initial;
    }
  }, [teamMembers.value.length]);

  // Computed: Anzahl pro Status
  const presentCount = computed(() => {
    let count = 0;
    rosterSelection.value.forEach((reason) => {
      if (reason === 'present') count++;
    });
    return count;
  });

  const absentCount = computed(() => {
    let count = 0;
    rosterSelection.value.forEach((reason) => {
      if (reason !== 'present') count++;
    });
    return count;
  });

  // Status-Toggle Handler
  function handleStatusChange(employeeId: string, reason: AbsenceReason) {
    setRosterSelection(employeeId, reason);
  }

  // Start Button Handler
  async function handleStart() {
    const roster = getRosterEntries();
    const success = await startDay(roster);
    if (success && onStarted) {
      onStarted();
    }
  }

  // Render
  if (teamMembers.value.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Users size={48} className={styles.emptyIcon} />
          <p className={styles.emptyText}>Keine Teammitglieder verfügbar</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Team-Anwesenheit</h2>
          <p className={styles.subtitle}>Wer ist heute dabei?</p>
        </div>
      </header>

      <div className={styles.employeeList}>
        {teamMembers.value.map((employee) => {
          const currentStatus = rosterSelection.value.get(employee.id) ?? 'present';

          return (
            <div key={employee.id} className={styles.employeeCard}>
              <div className={styles.employeeInfo}>
                <span className={styles.employeeName}>
                  {employee.firstName} {employee.lastName}
                </span>
                <span className={styles.employeeNumber}>
                  #{employee.personnelNumber}
                </span>
              </div>

              <div className={styles.statusToggle}>
                <button
                  type="button"
                  className={styles.statusOption}
                  data-status="present"
                  data-active={currentStatus === 'present'}
                  onClick={() => handleStatusChange(employee.id, 'present')}
                  title="Anwesend"
                >
                  <UserCheck size={18} />
                </button>
                <button
                  type="button"
                  className={styles.statusOption}
                  data-status="sick"
                  data-active={currentStatus === 'sick'}
                  onClick={() => handleStatusChange(employee.id, 'sick')}
                  title="Krank"
                >
                  Krank
                </button>
                <button
                  type="button"
                  className={styles.statusOption}
                  data-status="vacation"
                  data-active={currentStatus === 'vacation'}
                  onClick={() => handleStatusChange(employee.id, 'vacation')}
                  title="Urlaub"
                >
                  Urlaub
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <footer className={styles.footer}>
        <div className={styles.stats}>
          <span>{presentCount.value} anwesend</span>
          {absentCount.value > 0 && <span>{absentCount.value} abwesend</span>}
        </div>
        <SwipeButton
          label="Arbeitstag starten"
          loadingLabel="Wird gestartet..."
          onConfirm={handleStart}
          disabled={presentCount.value === 0}
          loading={isLoading.value}
          variant="success"
        />
      </footer>
    </div>
  );
}
